import { cva } from '@betterment/samba-styled-system/css';
import {
  Link,
  type LinkProps,
  type Location,
  NavLink as ReactRouterNavLink,
  useLocation,
  useResolvedPath,
} from 'react-router-dom';

import { Icon } from './Icon';
import { Subtext } from './Subtext';
import { Text } from './Text';

export const navLinkStyles = cva({
  base: {
    borderRadius: 'medium',
    px: 'space.150',
    color: 'currentColor',
    display: 'flex',
    alignItems: 'center',
    gap: 'space.150',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:has(+ &)': {
      mb: 'space.25',
    },
    '&[aria-current=page], &[aria-current=true], &[data-active="true"]': {
      backgroundColor: 'primitives.white.100alpha30',
      _hover: { backgroundColor: 'primitives.white.100alpha30' },
    },
    _hover: { backgroundColor: 'primitives.white.100alpha15' },
  },
  variants: {
    spacing: {
      compact: { py: 'space.50' },
      default: { py: 'space.100' },
      expanded: { py: 'space.150' },
    },
  },
  defaultVariants: {
    spacing: 'default',
  },
});

type Props = LinkProps & {
  activeWhen?: (location: Location) => boolean;
};

export const Root: React.FC<React.PropsWithChildren<Props>> = ({ activeWhen, children, to, ...rest }) => {
  const location = useLocation();
  const resolvedPath = useResolvedPath(to);
  const isExactMatch = location.pathname === resolvedPath.pathname;

  const isActive = activeWhen?.(location) ?? false;

  if (!isActive || isExactMatch) {
    return (
      <ReactRouterNavLink className={navLinkStyles()} to={to} {...rest}>
        {children}
      </ReactRouterNavLink>
    );
  }

  return (
    <Link className={navLinkStyles()} to={to} aria-current="true" data-active={true} {...rest}>
      {children}
    </Link>
  );
};

export const NavLink = Object.assign(Root, {
  Icon,
  Subtext,
  Text,
});
